
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useChatSession, ChatMessage } from "@/hooks/useChatSession";
import ChatHeader from "./ChatHeader";
import ChatMessageList from "./ChatMessageList";
import ChatInput from "./ChatInput";
import { Loader2, ChevronLeft, PlusCircle, MessageSquareText, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductTemplateSelector from "./ProductTemplateSelector";

interface ProductCopilotInterfaceProps {
  promptId: string | null;
  onClearPrompt?: () => void;
  showTemplateGrid?: boolean;
  setShowTemplateGrid?: (show: boolean) => void;
}

const ProductCopilotInterface = ({ 
  promptId, 
  onClearPrompt,
  showTemplateGrid = false,
  setShowTemplateGrid
}: ProductCopilotInterfaceProps) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session');
  
  const {
    isLoading,
    isLoadingPrompt,
    promptContent,
    promptTitle,
    sessionId,
    messages,
    isSavingToFavorites,
    handleSaveToFavorites,
    sendMessage
  } = useChatSession(sessionIdParam, promptId, user?.id || null);
  
  // Show a loading state when fetching prompt data
  if (isLoadingPrompt && promptId) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading template...</p>
        </div>
      </div>
    );
  }
  
  if (showTemplateGrid && setShowTemplateGrid) {
    return (
      <div className="h-full flex flex-col p-4">
        <div className="mb-6">
          <h2 className="text-xl font-semibold">Select a Template</h2>
          <p className="text-muted-foreground text-sm">
            Choose a template to start your conversation or start a free-form chat
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-y-auto mb-4">
          <div className="border border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center text-center h-40 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setShowTemplateGrid(false)}>
            <MessageSquareText className="h-8 w-8 mb-2 text-muted-foreground" />
            <h3 className="font-medium">Free-form Chat</h3>
            <p className="text-sm text-muted-foreground">Start a conversation without a template</p>
          </div>
          
          <div className="border border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center text-center h-40 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setShowTemplateGrid(true)}>
            <Sparkles className="h-8 w-8 mb-2 text-muted-foreground" />
            <h3 className="font-medium">Template Library</h3>
            <p className="text-sm text-muted-foreground">Browse our curated templates</p>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          <h3 className="text-md font-medium mb-3">Popular Templates</h3>
          <ProductTemplateSelector
            onPromptSelect={(id) => {
              if (setShowTemplateGrid) setShowTemplateGrid(false);
              if (onClearPrompt) onClearPrompt();
              // Wait for state to update before setting new prompt
              setTimeout(() => {
                window.location.href = `/product-copilot?prompt=${id}`;
              }, 100);
            }}
            selectedPrompt={promptId}
            filterType="recent"
            displayType="grid"
          />
        </div>
      </div>
    );
  }
  
  return (
    <>
      <ChatHeader
        promptTitle={promptTitle || "New Conversation"}
        promptId={promptId}
        sessionId={sessionId}
        onSaveToFavorites={handleSaveToFavorites}
        isSavingToFavorites={isSavingToFavorites}
        onBackToTemplates={setShowTemplateGrid ? () => setShowTemplateGrid(true) : undefined}
      />
      
      <ChatMessageList
        messages={messages}
        promptId={promptId}
      />
      
      <ChatInput
        onSubmit={sendMessage}
        isLoading={isLoading}
        disabled={false}
        isUsingTemplate={!!promptId}
        onRequestTemplates={setShowTemplateGrid ? () => setShowTemplateGrid(true) : undefined}
      />
    </>
  );
};

export default ProductCopilotInterface;
